import { inject, Injectable }      from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter }                  from 'rxjs/operators';
import { Observable, of }          from 'rxjs';

import { TimeoutService } from './session/timeout/timeout.service';
import { RestService }    from './rest.service';
import { environment }    from '../../environments/environment';

const ACTIVITY_REPORTING_INTERVAL = 20;

enum IframeMessageEvents {
  INIT = 'dcpInit',
  INIT_ACK = 'initAck',
  RENEW_SESSION = 'renewClientSession',
  RESIZE_IFRAME = 'resizeIframeWindow',
  SESSION_EXPIRED = 'dcpSessionExpired'
}

@Injectable({
  providedIn: 'root'
})
export class IframeService {
  parentOrigin: string;
  previousHeight = 0;
  private timeoutService = inject(TimeoutService);
  private router = inject(Router);
  private restService = inject(RestService);

  static runsInIframe() {
    try {
      return window.self !== window.top;
    } catch (_e) {
      return true;
    }
  }

  init() {
    if (IframeService.runsInIframe()) {
      environment['embedded'] = true;

      window.addEventListener('message', (message) => this.messageHandler(message));
      window.parent.postMessage({fnToCall: IframeMessageEvents.INIT}, '*');
    }
  }

  reportHeightChangeToParent(): void {
    if (this.parentOrigin) {
      const currentHeight = this.computeHeight();
      if (currentHeight !== this.previousHeight) {
        this.sendHeightMessageToParent(currentHeight + 'px');
        this.previousHeight = currentHeight;
      }
    }
  }

  private messageHandler(message: MessageEvent) {
    this.checkOrigin(message.origin)
      .pipe(filter((allowOrigin) => allowOrigin))
      .subscribe(() => {
        const event = message.data.fnToCall;
        switch (event) {
          case IframeMessageEvents.INIT_ACK:
            this.initIframeFeatures(message.origin);
            break;
          case IframeMessageEvents.RENEW_SESSION:
            this.timeoutService.keepSessionActive();
            break;
        }
      });
  }

  private checkOrigin(messageOrigin: string): Observable<boolean> {
    const hasExpectedOrigin = this.parentOrigin && messageOrigin === this.parentOrigin;
    return hasExpectedOrigin
      ? of(hasExpectedOrigin)
      : this.restService.post(environment.api.endpoints.iframeHostAllowed, messageOrigin);
  }

  private initIframeFeatures(origin: string) {
    this.parentOrigin = origin;

    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.sendHeightMessageToParent('100%');
        this.previousHeight = 0;
      });

    setInterval(() => this.sendUserActivityMessageToParent(), ACTIVITY_REPORTING_INTERVAL * 1000); /* 20 seconds */
    this.timeoutService.observeSessionExpired().subscribe(
      () => this.sendMessageToParent({fnToCall: IframeMessageEvents.SESSION_EXPIRED}));
  }

  private sendMessageToParent(message: unknown): void {
    window.parent.postMessage(message, this.parentOrigin);
  }

  private sendHeightMessageToParent(height: string) {
    this.sendMessageToParent({fnToCall: IframeMessageEvents.RESIZE_IFRAME, height: height});
  }

  private sendUserActivityMessageToParent(): void {
    if (this.parentOrigin
      && (Date.now() - this.timeoutService.getLastUserEvent() < (ACTIVITY_REPORTING_INTERVAL + 1) * 1000)) {
      this.sendMessageToParent({fnToCall: IframeMessageEvents.RENEW_SESSION});
    }
  }

  private computeHeight(): number {
    let height = 150;
    document.querySelectorAll('.container').forEach((e: HTMLElement) => height += e.offsetHeight);
    return height;
  }
}
